import { useSelector } from "react-redux";
import "./AppDocuments.css";
import { selectUser } from "../../../redux/features/appData";
import { useEffect, useState } from "react";
import { CompanyDocument, downloadFile } from "winslow-core";
import { apiDeleteCompanyDocument, apiGetCompanyDocumentsForWorkspace, apiUpdateCompanyDocument, apiUploadCompanyDocumentsForWorkspace } from "../../../services/CompanyDocumentsService";
import { AppDocumentsUploadButton } from "./AppDocumentsUploadButton";
import { AppChanges } from "../app-changes/AppChanges";
import toast from "react-hot-toast";
import { toastFail } from "../../../config";
import { useNavigate } from "react-router-dom";
import DeleteDocumentConfirmModal from "./DeleteDocumentConfirmModal";
import { set } from "lodash";

type AppDocumentsProps = {
}

type UploadInfo = {
    name: string;
    description: string;
    requiresSignatures: boolean;
}

export const AppDocuments = ({}: AppDocumentsProps) => {

    const navigate = useNavigate();

    const storeUser = useSelector(selectUser);

    const [documents, setDocuments] = useState<CompanyDocument[] | undefined>(undefined);
    const [addDocument, setAddDocument] = useState<boolean>(false);
    const [info, setInfo] = useState<UploadInfo>({
        name: "",
        description: "",
        requiresSignatures: false,
    });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const [editDocument, setEditDocument] = useState<boolean>(false);
    const [edittingDocument, setEdittingDocument] = useState<CompanyDocument | null>(null);

    const [deletingDocument, setDeletingDocument] = useState<CompanyDocument | null>(null);
    const [verifyConfirm, setVerifyConfirm] = useState<boolean>(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);

    useEffect(() => {
        if (!storeUser?.currentWorkspaceId) {
            console.log("No workspace selected");
            return;
        }

        (async () => {
            const documents = await apiGetCompanyDocumentsForWorkspace(storeUser.currentWorkspaceId || "");

            if (documents) {
                documents.sort((a, b) => {
                    return a.name.localeCompare(b.name);    
                })

                setDocuments(documents);
            }
        })();
    }, [storeUser]);

    const handleSelectedFile = (file: File) => {
        console.log("File selected", file);
        setSelectedFile(file);
        setHasChanges(true);
    };

    const handleUploadSubmit = async () => {
        if (!storeUser?.currentWorkspaceId) {
            console.log("No workspace selected");
            return;
        }

        if (!selectedFile) {
            console.log("No file selected");
            return;
        }

        if (!info.name) {
            console.log("No name provided");
            return;
        }

        const payload: Partial<CompanyDocument> = {
            workspaceId: storeUser.currentWorkspaceId,
            name: info.name,
            description: info.description,
            requiresSignatures: info.requiresSignatures, 
        }

        // push the file 
        const response = await apiUploadCompanyDocumentsForWorkspace(
            storeUser.currentWorkspaceId, payload, selectedFile);

        if (!response) {
            console.log("Error creating company document");
            toast("Error creating company document", toastFail);
            return;
        }

        const companyDocuments = response.data.data as CompanyDocument[];

        console.log("[AppDocuments] companyDocuments", companyDocuments);

        // console.log("Company documents created", companyDocuments);

        // if (!documents) {
        //     setDocuments([companyDocuments]);
        // } else {
        //     setDocuments([...documents, companyDocuments]);
        // }

        setHasChanges(false);
        setAddDocument(false);
        setInfo({
            name: "",
            description: "",
            requiresSignatures: false,
        });

        const updatedDocuments = documents ? [...documents, ...companyDocuments] : companyDocuments;
        updatedDocuments.sort((a, b) => {
            return a.name.localeCompare(b.name);    
        }); 
        setDocuments(updatedDocuments);
    }
    
    const handleDownload = async (doc: CompanyDocument) => {
        if (!doc.cloudURL) {
            console.log("No cloud URL for document");
            return;
        }

        downloadFile(doc.cloudURL, doc.fileName || "document");
    }

    const handleEditDocument = (doc: CompanyDocument) => {
        setInfo({
            name: doc.name,
            description: doc.description || "",
            requiresSignatures: doc.requiresSignatures || false,
        });
        setEditDocument(true);
        setEdittingDocument(doc);
    }

    const handleUpdateDocument = async () => {
        if (!storeUser?.currentWorkspaceId) {
            console.log("No workspace selected");
            return;
        }

        if (!edittingDocument?.id) {
            console.log("No document id");
            return;
        }

        const payload: Partial<CompanyDocument> = {
            id: edittingDocument.id,
            name: info.name,
            description: info.description,
            requiresSignatures: info.requiresSignatures, 
        }

        // push the file 
        const updatedDocument = await apiUpdateCompanyDocument(payload);

        if (!updatedDocument) {
            console.log("Error updating company document");
            toast("Error updating company document", toastFail);
            return;
        }

        setHasChanges(false);
        setAddDocument(false);
        setEditDocument(false);
        setInfo({
            name: "",
            description: "",
            requiresSignatures: false,
        });


        const updatedDocuments = documents ? 
            [...documents.filter((document: CompanyDocument) => {
                return document.id !== updatedDocument.id 
            }), updatedDocument] : 
            [updatedDocument];

        updatedDocuments.sort((a, b) => {
            return a.name.localeCompare(b.name);    
        }); 
        setDocuments(updatedDocuments);
    }


    const handleDeleteDocument = async (doc: CompanyDocument) => {
        if (!storeUser?.currentWorkspaceId) {
            console.log("No workspace selected");
            return;
        }

        if (!doc.id) {
            console.log("No document id");
            return;
        }

        const response = await apiDeleteCompanyDocument(doc.id);

        if (!response) {
            console.log("Error deleting company document");
            toast("Error deleting company document", toastFail);
            return;
        }

        const updatedDocuments = documents?.filter((d) => d.id !== doc.id);
        setDocuments(updatedDocuments);
    }

    console.log("[AppDocuments] documents", documents);

    return (
        <div className="app-documents">
            <h1>App Documents</h1>

            <button 
                className="button-save"
                onClick={() => {
                    setAddDocument(!addDocument);
                }}
            >
                {addDocument ? "Cancel" : "Add Document"}
            </button>

            { (addDocument || editDocument) &&
                <div className="app-documents-add">
                    <h2>Add Document</h2>

                    <label>Name</label>
                    <input 
                        type="text"
                        value={info.name}
                        placeholder="Document Name" 
                        onChange={(e) => {
                            setInfo({
                                ...info,
                                name: e.target.value
                            });
                            setHasChanges(true);
                        }}
                    />  

                    <label>Description</label>
                    <input 
                        type="text"
                        value={info.description}
                        placeholder="Desc" 
                        onChange={(e) => {
                            setInfo({
                                ...info,
                                description: e.target.value
                            });
                            setHasChanges(true);
                        }}
                    />  

                    <label>Signatures</label>
                    <input 
                        type="checkbox"
                        placeholder="Signatures"
                        checked={info.requiresSignatures}
                        onChange={(e) => {
                            setInfo({
                                ...info,
                                requiresSignatures: e.target.checked
                            });
                            setHasChanges(true);

                        }}  
                    />


                    <label>Upload File</label>
                    {editDocument &&
                        <div className="app-documents-edit">
                            {edittingDocument?.fileName}
                            <button 
                                className="button-save"
                                onClick={handleUpdateDocument}
                            >
                                Update
                            </button>
                        </div>
                    }
                    {addDocument &&
                        <AppDocumentsUploadButton
                            label="Select File"
                            disabled={editDocument}
                            onFileSelect={handleSelectedFile}
                        />
                    }
                </div>
            }

            {documents && documents.length > 0 && (
                <div className="app-documents-grid">
                    <div className="app-documents-grid-header-item">
                        Name 
                    </div>
                    <div className="app-documents-grid-header-item">
                        Description
                    </div>
                    <div className="app-documents-grid-header-item">
                        Filename
                    </div>
                    <div className="app-documents-grid-header-item">
                        Requires Signatures
                    </div>
                    <div className="app-documents-grid-header-item">
                        Actions
                    </div>
                    {documents.map((doc) => (
                        <>
                            <div className="app-documents-grid-item">
                                <a onClick={()=>handleDownload(doc)}>
                                    {doc.name}
                                </a>
                            </div>
                            <div className="app-documents-grid-item">
                                {doc.description || "N/A"}
                            </div>   
                            <div className="app-documents-grid-item">
                                {doc.fileName || "N/A"}
                            </div>
                            <div className="app-documents-grid-item">
                                {doc.requiresSignatures ? "Yes" : "No"}
                            </div>     
                            <div className="app-documents-grid-item">
                                <button 
                                    className="button-cancel"
                                    onClick={()=> handleEditDocument(doc)}
                                >
                                    Edit
                                </button>  
                                <button 
                                    className="button-cancel"
                                    onClick={()=> {
                                        setVerifyConfirm(true);
                                        setDeletingDocument(doc);
                                    }}
                                >
                                    Delete
                                </button>                            
                            </div>           
                         </>
                    ))}
                </div>
            )}

            
            { documents && documents.length > 0 &&
                <>
                </>
                
            }
            { !documents || (documents && documents.length === 0) && !addDocument &&
                <div className="app-documents-zero-state">
                    <h2>No documents found</h2>
                    <p>You can upload documents to your workspace.</p>
                </div>
            }

            { verifyConfirm &&
                <DeleteDocumentConfirmModal 
                    isOpen={verifyConfirm}
                    onClose={() => setVerifyConfirm(false)}
                    onContinue={() => {
                        if (deletingDocument) {
                            handleDeleteDocument(deletingDocument) 
                        }
                        setVerifyConfirm(false);
                    }}
                />
            }

            {hasChanges &&
                <AppChanges 
                    submitText={addDocument ? "Add Document" : "Update"}
                    onSubmit={handleUploadSubmit}
                    onCancel={()=> setAddDocument(false)}
                />
            }
        </div>
    );
};
